import { useEffect, useRef, useState } from "react";
import { Heart, Smile } from "lucide-react";
import { REACTIONS } from "../../constants/reactions";
import useClickOutside from "../../hooks/useClickOutside";

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

// Like, reaction chips, reaction picker, Reply, and owner Edit/Delete for one
// comment. Wraps onto extra lines so it stays usable at the deepest inline
// indentation. Clicking a reaction chip toggles that reaction — the same as
// picking it from the picker (add, switch, or remove).
//
// When `canInteract` is false (expired post, canceled occurrence) existing
// counts are shown read-only and there is no picker. `canRemoveOwn` still lets
// the viewer take back their own like or reaction (a canceled occurrence).
export default function CommentInteractionBar({
  comment,
  canInteract,
  canRemoveOwn = false,
  isReactionPending,
  onToggleLike,
  onReact,
  onReply,
  onEdit,
  onDelete,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerButtonRef = useRef(null);

  const interactive = canInteract && !comment.is_deleted;
  const removable = !interactive && canRemoveOwn && !comment.is_deleted;
  const likeInteractive = interactive || (removable && !!comment.is_liked);
  const likesCount = comment.likes_count || 0;
  const reactionCounts = comment.reaction_counts || {};
  const chips = REACTIONS.map((r) => ({ ...r, count: reactionCounts[r.type] || 0 })).filter(
    (r) => r.count > 0
  );

  const handlePick = (type) => {
    setPickerOpen(false);
    onReact(type);
    pickerButtonRef.current?.focus();
  };

  const linkButton = "rounded hover:text-gray-800 focus-visible:outline focus-visible:outline-2";

  return (
    <div className="mt-1">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
        {likeInteractive ? (
          <button
            type="button"
            onClick={onToggleLike}
            aria-pressed={!!comment.is_liked}
            aria-label={`${comment.is_liked ? "Unlike" : "Like"} comment, ${plural(likesCount, "like")}`}
            className={`flex items-center gap-1 rounded ${
              comment.is_liked ? "text-red-500" : "hover:text-red-500"
            }`}
          >
            <Heart size={14} fill={comment.is_liked ? "currentColor" : "none"} aria-hidden="true" />
            {likesCount > 0 && <span>{likesCount}</span>}
          </button>
        ) : (
          likesCount > 0 && (
            <span className="flex items-center gap-1" aria-label={plural(likesCount, "like")}>
              <Heart size={14} aria-hidden="true" />
              <span aria-hidden="true">{likesCount}</span>
            </span>
          )
        )}

        {chips.map((r) => {
          const isMine = comment.my_reaction === r.type;
          const label = `${plural(r.count, `${r.label} reaction`)}${isMine ? ", including yours" : ""}`;
          const chipClass = `flex items-center gap-1 rounded-full px-1.5 py-0.5 ${
            isMine ? "bg-blue-50 text-blue-600 ring-1 ring-blue-300" : ""
          }`;
          return interactive || (removable && isMine) ? (
            <button
              key={r.type}
              type="button"
              onClick={() => onReact(r.type)}
              disabled={isReactionPending}
              aria-pressed={isMine}
              aria-label={`${label}. ${isMine ? "Remove your reaction" : `React with ${r.label}`}`}
              className={`${chipClass} hover:bg-gray-100 disabled:cursor-wait disabled:opacity-60`}
            >
              <span aria-hidden="true">{r.emoji}</span>
              <span aria-hidden="true">{r.count}</span>
            </button>
          ) : (
            <span key={r.type} className={chipClass} aria-label={label}>
              <span aria-hidden="true">{r.emoji}</span>
              <span aria-hidden="true">{r.count}</span>
            </span>
          );
        })}

        {interactive && (
          <button
            ref={pickerButtonRef}
            type="button"
            onClick={() => setPickerOpen((open) => !open)}
            disabled={isReactionPending}
            aria-expanded={pickerOpen}
            aria-label={comment.my_reaction ? "Change reaction" : "Add reaction"}
            className={`flex items-center rounded disabled:cursor-wait disabled:opacity-60 ${
              comment.my_reaction ? "text-blue-600" : "hover:text-blue-500"
            }`}
          >
            <Smile size={14} aria-hidden="true" />
          </button>
        )}

        {onReply && (
          <button type="button" onClick={onReply} className={linkButton}>
            Reply
          </button>
        )}
        {onEdit && (
          <button type="button" onClick={onEdit} className={linkButton}>
            Edit
          </button>
        )}
        {onDelete && (
          <button type="button" onClick={onDelete} className={`${linkButton} hover:text-red-600`}>
            Delete
          </button>
        )}
      </div>

      {interactive && pickerOpen && (
        <ReactionPickerRow
          myReaction={comment.my_reaction}
          onPick={handlePick}
          onClose={() => setPickerOpen(false)}
          toggleRef={pickerButtonRef}
        />
      )}
    </div>
  );
}

// Rendered inline under the bar (not as a floating popover) so it never
// overflows the viewport at deep indentation or on narrow screens. Only
// mounted while open, so rows don't each hold document listeners.
function ReactionPickerRow({ myReaction, onPick, onClose, toggleRef }) {
  const rowRef = useRef(null);
  useClickOutside([rowRef, toggleRef], onClose);

  // Move focus into the picker (current reaction, else the first) so keyboard
  // users don't have to tab past Reply/Edit/Delete to reach it.
  useEffect(() => {
    const row = rowRef.current;
    (row?.querySelector('button[aria-pressed="true"]') || row?.querySelector("button"))?.focus();
  }, []);

  return (
    <div
      ref={rowRef}
      role="group"
      aria-label="Choose a reaction"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          onClose();
          toggleRef.current?.focus();
        }
      }}
      className="mt-1 inline-flex flex-wrap items-center gap-1 rounded-full border border-gray-200 bg-white px-1.5 py-1 shadow-sm"
    >
      {REACTIONS.map((r) => (
        <button
          key={r.type}
          type="button"
          onClick={() => onPick(r.type)}
          aria-pressed={myReaction === r.type}
          aria-label={myReaction === r.type ? `Remove ${r.label} reaction` : `React with ${r.label}`}
          className={`flex h-7 w-7 items-center justify-center rounded-full text-base leading-none hover:scale-110 hover:bg-gray-100 ${
            myReaction === r.type ? "bg-blue-50 ring-1 ring-blue-300" : ""
          }`}
        >
          {r.emoji}
        </button>
      ))}
    </div>
  );
}
